import React from "react";
import { GarmentTypeManager } from "./GarmentTypeManager";
import { Edit, Plus, Power, Save, Search, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  deleteFamily,
  deleteMaterial,
  deleteMaterialSupplier,
  deleteProcess,
  deleteSeason,
  familyTariffParamsEqual,
  fetchFamilies,
  fetchMaterialSuppliers,
  fetchMaterials,
  fetchProcesses,
  fetchSeasons,
  fetchSuppliers,
  FAMILY_TARIFF_OPTIONS,
  formatMoney,
  getOtherFamiliesForTariff,
  groupFamiliesByTariffParams,
  saveFamily,
  saveMaterial,
  saveMaterialSupplier,
  saveProcess,
  saveSeason,
  syncFamiliesTariffParams,
} from "@/lib/api/manufacturing";

const initialFamily = {
  magnet_label_color: "",
  cod_fam: "",
  descrip: "",
  iniciales: "",
  tarifa: "",
  valor_fijo: 0,
  incremento: 0,
  condicion: 0,
};

function familyTariffSummary(family) {
  if (!family?.tarifa) return "Sin tarifa de recálculo";
  return `Tarifa ${family.tarifa} · fijo ${Number(family.valor_fijo || 0)} · +${Number(family.incremento || 0)}% · cond. ${Number(family.condicion || 0)}`;
}

function familyLabel(family) {
  return `${family.cod_fam?.trim() || ""} · ${family.descrip || "Sin descripción"}`;
}

const initialSeason = {
  cod_tem: "",
  descrip: "",
  codigo: "",
  defecto: false,
  fecha_i: "",
  fecha_f: "",
};

const initialProcess = {
  planning_order: "",
  allows_color_variants: false,
  document_requirements: [],
  requires_documents: false,
  document_stage: "pattern",
  required_document_count: 1,
  price_stage: "",
  consumption_stage: "",
  albaran_cutting_layout: false,
  albaran_material_process_id: "",
  legacy_code: "",
  description: "",
  notes: "",
  excludes_manufacturing: false,
  is_overhead: false,
  is_active: true,
};

const initialMaterial = {
  hide_from_planning: false,
  code: "",
  internal_description: "",
  season_code: "",
  default_unit: "ud",
  is_active: true,
};

const initialMaterialSupplier = {
  supplier_id: "",
  supplier_ref: "",
  supplier_description: "",
  purchase_price: 0,
  currency: "EUR",
  is_primary: false,
  valid_from: "",
  valid_until: "",
};

function ToggleField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

function FamilyManager() {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [families, setFamilies] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [editingCodFam, setEditingCodFam] = React.useState(null);
  const [formData, setFormData] = React.useState(initialFamily);
  const [loading, setLoading] = React.useState(false);
  const [unifyConflict, setUnifyConflict] = React.useState(null);
  const [pendingSyncTarifa, setPendingSyncTarifa] = React.useState(null);
  const { toast } = useToast();

  const loadFamilies = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchFamilies(searchTerm);
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar las familias", variant: "destructive" });
    }
    setFamilies(result.data || []);
    setLoading(false);
  }, [searchTerm, toast]);

  React.useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  const reset = () => {
    setEditorOpen(false);
    setEditingCodFam(null);
    setFormData(initialFamily);
    setUnifyConflict(null);
    setPendingSyncTarifa(null);
  };

  const buildUnifyConflict = (payload, { source, previousTarifa } = {}) => {
    const otherFamilies = getOtherFamiliesForTariff(families, payload.tarifa, editingCodFam || payload.cod_fam);
    const conflicts = otherFamilies.filter((family) => !familyTariffParamsEqual(family, payload));
    if (!conflicts.length) return null;
    return {
      source,
      previousTarifa,
      tarifa: payload.tarifa,
      conflicts,
      otherGroups: groupFamiliesByTariffParams(otherFamilies),
    };
  };

  const persistFamily = async (payload, { sync = false } = {}) => {
    const result = await saveFamily(payload, editingCodFam);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar la familia", variant: "destructive" });
      return false;
    }
    if (sync && payload.tarifa) {
      const syncResult = await syncFamiliesTariffParams(payload.tarifa, payload);
      if (syncResult.error) {
        toast({
          title: "Familia guardada",
          description: syncResult.error.message || "No se pudieron unificar el resto de familias de esa tarifa",
          variant: "destructive",
        });
        await loadFamilies();
        return false;
      }
    }
    toast({ title: editingCodFam ? "Familia actualizada" : "Familia creada" });
    reset();
    await loadFamilies();
    return true;
  };

  const submit = async (event) => {
    event.preventDefault();
    const codFam = formData.cod_fam.trim().toUpperCase();
    if (!codFam || !formData.descrip.trim()) {
      toast({ title: "Validación", description: "Código y descripción son obligatorios", variant: "destructive" });
      return;
    }
    if (codFam.length > 4) {
      toast({ title: "Validación", description: "El código de familia no puede superar 4 caracteres", variant: "destructive" });
      return;
    }

    const payload = { ...formData, cod_fam: codFam };
    const shouldSync = pendingSyncTarifa != null && String(payload.tarifa) === String(pendingSyncTarifa);
    const conflict = shouldSync ? null : buildUnifyConflict(payload, { source: "save" });
    if (conflict) {
      setUnifyConflict(conflict);
      return;
    }
    await persistFamily(payload, { sync: shouldSync });
  };

  const changeTarifa = (tarifa) => {
    const previousTarifa = formData.tarifa;
    const nextForm = { ...formData, tarifa };
    setFormData(nextForm);
    const conflict = buildUnifyConflict(nextForm, { source: "tarifa", previousTarifa });
    setUnifyConflict(conflict);
    if (conflict) setPendingSyncTarifa(null);
  };

  const cancelUnify = () => {
    if (unifyConflict?.source === "tarifa") {
      setFormData((current) => ({ ...current, tarifa: unifyConflict.previousTarifa }));
    }
    setUnifyConflict(null);
  };

  const applyMyParamsToAll = async () => {
    const payload = { ...formData, cod_fam: formData.cod_fam.trim().toUpperCase() };
    if (unifyConflict?.source === "tarifa") {
      setPendingSyncTarifa(unifyConflict.tarifa);
      setUnifyConflict(null);
      return;
    }
    setUnifyConflict(null);
    await persistFamily(payload, { sync: true });
  };

  const adoptParamGroup = async (group) => {
    const nextForm = {
      ...formData,
      valor_fijo: group.valor_fijo,
      incremento: group.incremento,
      condicion: group.condicion,
    };
    setFormData(nextForm);
    setUnifyConflict(null);
    if (unifyConflict?.source === "save") {
      await persistFamily({ ...nextForm, cod_fam: nextForm.cod_fam.trim().toUpperCase() }, { sync: true });
    }
  };

  const edit = (family) => {
    setEditorOpen(true);
    setEditingCodFam(family.cod_fam);
    setFormData({
      magnet_label_color: family.magnet_label_color || "",
      cod_fam: family.cod_fam?.trim() || "",
      descrip: family.descrip || "",
      iniciales: family.iniciales?.trim() || "",
      tarifa: family.tarifa ?? "",
      valor_fijo: family.valor_fijo ?? 0,
      incremento: family.incremento ?? 0,
      condicion: family.condicion ?? 0,
    });
  };

  const remove = async (family) => {
    if (!window.confirm(`¿Eliminar la familia "${family.cod_fam?.trim()} - ${family.descrip}"?`)) return;
    const result = await deleteFamily(family.cod_fam);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo eliminar. Puede estar usada en artículos.", variant: "destructive" });
      return;
    }
    await loadFamilies();
  };

  return (
    <div className="space-y-4">
      <Button type="button" onClick={() => { reset(); setEditorOpen(true); }}><Plus className="mr-2 h-4 w-4" />Nueva familia</Button>
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingCodFam ? "Editar familia" : "Nueva familia"}</DialogTitle><DialogDescription>Modifica los datos y pulsa Guardar para conservar los cambios.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{editingCodFam ? "Editar familia" : "Nueva familia"}</h3>
          {editingCodFam && (
            <Button type="button" variant="ghost" onClick={reset}>
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          <Input
            required
            maxLength={4}
            placeholder="Código familia"
            value={formData.cod_fam}
            onChange={(event) => setFormData({ ...formData, cod_fam: event.target.value.toUpperCase() })}
          />
          <Input
            required
            className="md:col-span-3"
            placeholder="Descripción"
            value={formData.descrip}
            onChange={(event) =>
              setFormData((current) => ({
                ...current,
                descrip: event.target.value,
                iniciales: current.iniciales ? current.iniciales : event.target.value.slice(0, 2).toUpperCase(),
              }))
            }
          />
          <Input
            maxLength={2}
            placeholder="Iniciales (ref.)"
            value={formData.iniciales}
            onChange={(event) => setFormData({ ...formData, iniciales: event.target.value.toUpperCase() })}
          />
        </div>
        <div className="mt-4 space-y-2 rounded-md border p-3">
          <label className="flex items-center gap-3 text-sm">
            <span>Color del código en etiquetas imán</span>
            <Input type="color" className="h-10 w-16 cursor-pointer p-1" value={formData.magnet_label_color || '#000000'} onChange={(event) => setFormData({ ...formData, magnet_label_color: event.target.value })} />
          </label>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-bold" style={{ color: formData.magnet_label_color || '#000000' }}>ABC-001</span>
            <span>{formData.magnet_label_color || 'Sin asignar (negro)'}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, magnet_label_color: '' })}>Quitar color</Button>
          </div>
          <p className="text-xs text-gray-500">Se aplica únicamente al código impreso en las etiquetas imán de esta familia.</p>
        </div>
        <div className="mt-4 rounded-md border border-dashed bg-gray-50 p-3">
          <div className="mb-2 text-sm font-medium text-gray-800">Cálculo de tarifas</div>
          <p className="mb-3 text-xs text-gray-500">
            Parámetros legacy (valor fijo, incremento y condición) y la tarifa de venta a la que se aplicarán (1-6).
          </p>
          <div className="grid gap-3 md:grid-cols-4">
            <label className="space-y-1 text-sm">
              <span className="text-gray-600">Tarifa afectada</span>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={formData.tarifa}
                onChange={(event) => changeTarifa(event.target.value)}
              >
                <option value="">Sin asignar</option>
                {FAMILY_TARIFF_OPTIONS.map((tarifa) => (
                  <option key={tarifa} value={tarifa}>
                    Tarifa {tarifa}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-gray-600">Valor fijo</span>
              <Input
                type="number"
                step="0.01"
                value={formData.valor_fijo}
                onChange={(event) => setFormData({ ...formData, valor_fijo: event.target.value })}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-gray-600">Incremento %</span>
              <Input
                type="number"
                step="0.01"
                value={formData.incremento}
                onChange={(event) => setFormData({ ...formData, incremento: event.target.value })}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-gray-600">Condición</span>
              <Input
                type="number"
                step="0.01"
                value={formData.condicion}
                onChange={(event) => setFormData({ ...formData, condicion: event.target.value })}
              />
            </label>
          </div>
        </div>
        <Button className="mt-4" type="submit">
          <Save className="mr-2 h-4 w-4" />
          Guardar familia
        </Button>
      </form>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Buscar familia" />
        </div>
        <div className="space-y-2">
          {loading ? (
            <p>Cargando familias...</p>
          ) : families.length === 0 ? (
            <p className="text-sm text-gray-500">No hay familias.</p>
          ) : (
            families.map((family) => (
              <div key={family.cod_fam} className="flex flex-col justify-between gap-3 rounded border p-3 md:flex-row md:items-center">
                <div>
                  <div className="font-medium">{family.descrip}</div>
                  <div className="font-mono text-sm text-gray-600">
                    Código {family.cod_fam?.trim()}
                    {family.iniciales?.trim() ? ` · Iniciales ${family.iniciales.trim()} · ${family.contador || 0}` : ""}
                  </div>
                  <div className="text-xs text-gray-500">{familyTariffSummary(family)}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" title="Editar" onClick={() => edit(family)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => remove(family)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <AlertDialog open={Boolean(unifyConflict)} onOpenChange={(open) => !open && cancelUnify()}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Tarifa {unifyConflict?.tarifa} ya definida</AlertDialogTitle>
            <AlertDialogDescription>
              Otras familias usan esta tarifa con parámetros distintos. Unifica el criterio para que el recálculo sea el mismo en todos los artículos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-48 space-y-2 overflow-auto text-sm">
            {(unifyConflict?.otherGroups || []).map((group, index) => (
              <div key={`${group.valor_fijo}-${group.incremento}-${group.condicion}-${index}`} className="rounded border bg-gray-50 p-2">
                <div className="font-medium text-gray-800">
                  Fijo {Number(group.valor_fijo)} · +{Number(group.incremento)}% · cond. {Number(group.condicion)}
                </div>
                <div className="text-xs text-gray-600">{group.families.map(familyLabel).join(", ")}</div>
              </div>
            ))}
          </div>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button type="button" onClick={applyMyParamsToAll}>
              Usar mis valores en todas
            </Button>
            {(unifyConflict?.otherGroups || []).map((group, index) => (
              <Button
                key={`adopt-${group.valor_fijo}-${group.incremento}-${group.condicion}-${index}`}
                type="button"
                variant="outline"
                onClick={() => adoptParamGroup(group)}
              >
                {unifyConflict.otherGroups.length === 1
                  ? "Adoptar los ya definidos"
                  : `Adoptar ${familyLabel(group.families[0])}`}
              </Button>
            ))}
            <Button type="button" variant="ghost" onClick={cancelUnify}>
              Cancelar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SeasonManager() {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [seasons, setSeasons] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [editingCodTem, setEditingCodTem] = React.useState(null);
  const [formData, setFormData] = React.useState(initialSeason);
  const [loading, setLoading] = React.useState(false);
  const [defaultSeasonNotice, setDefaultSeasonNotice] = React.useState("");
  const [checkingDefaults, setCheckingDefaults] = React.useState(false);
  const { toast } = useToast();

  const loadSeasons = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchSeasons(searchTerm);
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar las temporadas", variant: "destructive" });
    }
    setSeasons(result.data || []);
    setLoading(false);
  }, [searchTerm, toast]);

  React.useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  React.useEffect(() => {
    let cancelled = false;
    setDefaultSeasonNotice("");
    setCheckingDefaults(false);
    if (!editorOpen || !formData.defecto) return;
    setCheckingDefaults(true);
    const checkDefaults = async () => {
      // Consult all seasons: the maintenance search may hide an existing default.
      const result = await fetchSeasons();
      if (cancelled) return;
      setCheckingDefaults(false);
      if (result.error) {
        setDefaultSeasonNotice("No se pudo comprobar si hay otras temporadas por defecto. Vuelve a marcar la casilla para reintentar.");
        return;
      }
      const others = (result.data || []).filter((season) => season.defecto
        && season.cod_tem?.trim() !== editingCodTem?.trim());
      if (others.length) {
        const names = others.map((season) => `${season.cod_tem.trim()} · ${season.descrip}`).join(", ");
        setDefaultSeasonNotice(`Ya hay otras temporadas por defecto: ${names}. Si guardas, esta temporada también quedará marcada. En el alta de artículos se seleccionará la primera según el orden del listado.`);
      }
    };
    checkDefaults();
    return () => { cancelled = true; };
  }, [editorOpen, formData.defecto, editingCodTem]);

  const reset = () => {
    setEditorOpen(false);
    setEditingCodTem(null);
    setFormData(initialSeason);
  };

  const submit = async (event) => {
    event.preventDefault();
    const codTem = formData.cod_tem.trim().toUpperCase();
    if (!codTem || !formData.descrip.trim()) {
      toast({ title: "Validación", description: "Código y descripción son obligatorios", variant: "destructive" });
      return;
    }
    if (codTem.length > 4) {
      toast({ title: "Validación", description: "El código de temporada no puede superar 4 caracteres", variant: "destructive" });
      return;
    }
    if (formData.fecha_i && formData.fecha_f && formData.fecha_i > formData.fecha_f) {
      toast({ title: "Validación", description: "La fecha inicial no puede ser posterior a la final", variant: "destructive" });
      return;
    }

    const result = await saveSeason({ ...formData, cod_tem: codTem }, editingCodTem);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar la temporada", variant: "destructive" });
      return;
    }
    toast({ title: editingCodTem ? "Temporada actualizada" : "Temporada creada" });
    reset();
    await loadSeasons();
  };

  const edit = (season) => {
    setEditorOpen(true);
    setEditingCodTem(season.cod_tem);
    setFormData({
      cod_tem: season.cod_tem?.trim() || "",
      descrip: season.descrip || "",
      codigo: season.codigo ?? "",
      defecto: Boolean(season.defecto),
      fecha_i: season.fecha_i || "",
      fecha_f: season.fecha_f || "",
    });
  };

  const remove = async (season) => {
    if (!window.confirm(`¿Eliminar la temporada "${season.cod_tem?.trim()} - ${season.descrip}"?`)) return;
    const result = await deleteSeason(season.cod_tem);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo eliminar. Puede estar usada en artículos.", variant: "destructive" });
      return;
    }
    await loadSeasons();
  };

  return (
    <div className="space-y-4">
      <Button type="button" onClick={() => { reset(); setEditorOpen(true); }}><Plus className="mr-2 h-4 w-4" />Nueva temporada</Button>
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingCodTem ? "Editar temporada" : "Nueva temporada"}</DialogTitle><DialogDescription>Modifica los datos y pulsa Guardar para conservar los cambios.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{editingCodTem ? "Editar temporada" : "Nueva temporada"}</h3>
          {editingCodTem && (
            <Button type="button" variant="ghost" onClick={reset}>
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-6">
          <Input
            required
            maxLength={4}
            placeholder="Código temporada"
            value={formData.cod_tem}
            onChange={(event) => setFormData({ ...formData, cod_tem: event.target.value.toUpperCase() })}
          />
          <Input
            required
            className="md:col-span-2"
            maxLength={30}
            placeholder="Descripción"
            value={formData.descrip}
            onChange={(event) => setFormData({ ...formData, descrip: event.target.value })}
          />
          <Input
            type="number"
            step="1"
            placeholder="Código legacy"
            value={formData.codigo}
            onChange={(event) => setFormData({ ...formData, codigo: event.target.value })}
          />
          <Input
            type="date"
            value={formData.fecha_i}
            onChange={(event) => setFormData({ ...formData, fecha_i: event.target.value })}
          />
          <Input
            type="date"
            value={formData.fecha_f}
            onChange={(event) => setFormData({ ...formData, fecha_f: event.target.value })}
          />
        </div>
        <div className="mt-3">
          <ToggleField label="Temporada por defecto" checked={formData.defecto} onChange={(value) => setFormData((current) => ({ ...current, defecto: value }))} />
          <p className="mt-1 text-xs text-gray-500">Se preseleccionará al dar de alta un artículo. Puedes marcarla o desmarcarla y guardar los cambios.</p>
          {checkingDefaults && <p role="status" className="mt-2 text-sm text-gray-500">Comprobando temporadas por defecto...</p>}
          {defaultSeasonNotice && <p role="alert" className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{defaultSeasonNotice}</p>}
        </div>
        <Button className="mt-4" type="submit" disabled={checkingDefaults}>
          <Save className="mr-2 h-4 w-4" />
          Guardar temporada
        </Button>
      </form>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Buscar temporada" />
        </div>
        <div className="space-y-2">
          {loading ? (
            <p>Cargando temporadas...</p>
          ) : seasons.length === 0 ? (
            <p className="text-sm text-gray-500">No hay temporadas.</p>
          ) : (
            seasons.map((season) => (
              <div key={season.cod_tem} className="flex flex-col justify-between gap-3 rounded border p-3 md:flex-row md:items-center">
                <div>
                  <div className="font-medium">
                    {season.descrip}
                    {season.defecto && <span className="ml-2 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700">Defecto</span>}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-mono">Código {season.cod_tem?.trim()}</span>
                    {season.codigo !== null && season.codigo !== undefined ? ` · Legacy ${season.codigo}` : ""}
                    {season.orden != null ? ` · Orden ${season.orden}` : ""}
                    {season.fecha_i || season.fecha_f ? ` · ${season.fecha_i || "..."} - ${season.fecha_f || "..."}` : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" title="Editar" onClick={() => edit(season)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => remove(season)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ProcessManager() {
  const [materialProcesses, setMaterialProcesses] = React.useState([]);

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [processes, setProcesses] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [editingId, setEditingId] = React.useState(null);
  const [formData, setFormData] = React.useState(initialProcess);
  const [loading, setLoading] = React.useState(false);
  const { toast } = useToast();

  const loadProcesses = React.useCallback(async () => {
    setLoading(true);
    const [result, choices] = await Promise.all([fetchProcesses({ searchTerm }), fetchProcesses({})]);
    if (choices.error) toast({ title: "Error", description: "No se pudieron cargar los procesos para el albarán", variant: "destructive" });
    else setMaterialProcesses(choices.data || []);
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar los procesos", variant: "destructive" });
    }
    setProcesses(result.data || []);
    setLoading(false);
  }, [searchTerm, toast]);

  React.useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  const reset = () => {
    setEditorOpen(false);
    setEditingId(null);
    setFormData(initialProcess);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!formData.description.trim()) {
      toast({ title: "Validación", description: "La descripción del proceso es obligatoria", variant: "destructive" });
      return;
    }
    const result = await saveProcess(formData, editingId);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar el proceso", variant: "destructive" });
      return;
    }
    toast({ title: editingId ? "Proceso actualizado" : "Proceso creado" });
    reset();
    await loadProcesses();
  };

  const edit = (process) => {
    setEditorOpen(true);
    setEditingId(process.id);
    setFormData({
      allows_color_variants: Boolean(process.allows_color_variants),
      document_requirements: process.document_requirements || [],
      requires_documents: Boolean(process.requires_documents),
      document_stage: process.document_stage || "pattern",
      required_document_count: process.required_document_count ?? 1,
      price_stage: process.price_stage || "",
      consumption_stage: process.consumption_stage || "",
      planning_order: process.planning_order ?? "",
      legacy_code: process.legacy_code || "",
      description: process.description || "",
      notes: process.notes || "",
      excludes_manufacturing: Boolean(process.excludes_manufacturing),
      is_overhead: Boolean(process.is_overhead),
      albaran_material_process_id: process.albaran_material_process_id || "",
      albaran_cutting_layout: Boolean(process.albaran_cutting_layout),
      is_active: process.is_active !== false,
    });
  };

  const remove = async (process) => {
    if (!window.confirm(`¿Eliminar el proceso "${process.description}"?`)) return;
    const result = await deleteProcess(process.id);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo eliminar. Puede estar usado en escandallos.", variant: "destructive" });
      return;
    }
    await loadProcesses();
  };

  return (
    <div className="space-y-4">
      <Button type="button" onClick={() => { reset(); setEditorOpen(true); }}><Plus className="mr-2 h-4 w-4" />Nuevo proceso</Button>
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar proceso" : "Nuevo proceso"}</DialogTitle><DialogDescription>Modifica los datos y pulsa Guardar para conservar los cambios.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{editingId ? "Editar proceso" : "Nuevo proceso"}</h3>
          {editingId && (
            <Button type="button" variant="ghost" onClick={reset}>
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <Input
            type="number"
            placeholder="Código legacy"
            value={formData.legacy_code}
            onChange={(event) => setFormData({ ...formData, legacy_code: event.target.value })}
          />
          <Input
            required
            className="md:col-span-2"
            placeholder="Descripción"
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
          />
          <Input
            placeholder="Notas"
            value={formData.notes}
            onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-4">
          <ToggleField
            label="Activo"
            checked={formData.is_active}
            onChange={(value) => setFormData({ ...formData, is_active: value })}
          />
          <ToggleField
            label="Estructura / gastos generales"
            checked={formData.is_overhead}
            onChange={(value) => setFormData({ ...formData, is_overhead: value })}
          />
          <ToggleField
            label="Ocultar en el seguimiento de fabricación (mantiene el coste)"
            checked={formData.excludes_manufacturing}
            onChange={(value) => setFormData({ ...formData, excludes_manufacturing: value })}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">La marca de estructura / gastos generales excluye el importe del proceso del coste directo de producción en simulaciones y órdenes. Se conserva en el escandallo y en el desglose del estudio de ventas; también se excluyen sus materiales asociados, incluida la merma.</p>
        <label className="mt-3 block text-sm">
          Orden en el cuadrante
          <Input type="number" min="1" max="2147483647" step="1" className="mt-1 max-w-48" placeholder="Sin orden"
            value={formData.planning_order} onChange={(event) => setFormData({ ...formData, planning_order: event.target.value })} />
          <span className="text-xs text-gray-500">De menor a mayor (1, 2, 5, 10…). Sin orden, al final.</span>
        </label>
        <div className="mt-3">
          <ToggleField label="Permite variantes de color"
            checked={formData.allows_color_variants}
            onChange={(value) => setFormData({ ...formData, allows_color_variants: value })} />
          <ToggleField label="Formato de albarán de corte (consumos en cm)"
            checked={formData.albaran_cutting_layout}
            onChange={(value) => setFormData({ ...formData, albaran_cutting_layout: value })} />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {[["price_stage", "Responsable de precio, proveedor y referencia"], ["consumption_stage", "Responsable de consumo de materiales"]].map(([field, label]) => (
            <label key={field} className="text-sm">{label}
              <select className="mt-1 block w-full rounded border p-2" value={formData[field]}
                onChange={(event) => setFormData({ ...formData, [field]: event.target.value })}>
                <option value="">Sin asignar</option><option value="design">Diseño</option>
                <option value="pattern">Patronaje</option><option value="production">Producción</option>
              </select>
            </label>
          ))}
        </div>
        <div className="mt-4 space-y-3 rounded border p-3">
          <ToggleField label="Requiere documentos" checked={formData.requires_documents}
            onChange={(value) => setFormData({ ...formData, requires_documents: value })} />
          {formData.requires_documents && !formData.document_requirements.length && <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm">Responsable de documentos
              <select className="mt-1 block w-full rounded border p-2" value={formData.document_stage}
                onChange={(event) => setFormData({ ...formData, document_stage: event.target.value })}>
                <option value="design">Diseño</option><option value="pattern">Patronaje</option><option value="production">Producción</option>
              </select>
            </label>
            <label className="text-sm">Documentos mínimos por proceso
              <Input type="number" min="1" step="1" required value={formData.required_document_count}
                onChange={(event) => setFormData({ ...formData, required_document_count: event.target.value })} />
            </label>
          </div>}
        </div>
        {formData.requires_documents && <div className="mt-3 space-y-3 rounded border p-3">
          <p className="text-sm">Documentos específicos (opcional). Cada requisito se cubre con un adjunto identificado y sustituye el mínimo por cantidad.</p>
          {formData.document_requirements.map((requirement, index) => <div key={requirement.id} className="grid gap-2 md:grid-cols-[1fr_10rem_auto]">
            <Input required aria-label={`Nombre del requisito ${index + 1}`} placeholder="Ej.: Documento de diseño" value={requirement.name}
              onChange={(event) => setFormData({ ...formData, document_requirements: formData.document_requirements.map((r) => r.id === requirement.id ? { ...r, name: event.target.value } : r) })} />
            <select aria-label={`Responsable del requisito ${index + 1}`} className="rounded border p-2" value={requirement.stage}
              onChange={(event) => setFormData({ ...formData, document_requirements: formData.document_requirements.map((r) => r.id === requirement.id ? { ...r, stage: event.target.value } : r) })}>
              <option value="design">Diseño</option><option value="pattern">Patronaje</option><option value="production">Producción</option>
            </select>
            <Button type="button" variant="outline" aria-label={`Quitar requisito ${index + 1}`}
              onClick={() => setFormData({ ...formData, document_requirements: formData.document_requirements.filter((r) => r.id !== requirement.id) })}>Quitar</Button>
          </div>)}
          <Button type="button" variant="outline" onClick={() => setFormData({ ...formData, document_requirements: [...formData.document_requirements, { id: crypto.randomUUID(), name: '', stage: formData.document_stage }] })}>Añadir requisito específico</Button>
          {!!formData.document_requirements.length && <p className="text-xs text-gray-500">Al quitar un requisito, sus documentos se conservan como adjuntos adicionales.</p>}
        </div>}
        <label className="mt-4 block text-sm">
          Incluir también en el albarán los materiales del proceso
          <select className="mt-1 block w-full rounded border p-2" value={formData.albaran_material_process_id}
            onChange={(event) => setFormData({ ...formData, albaran_material_process_id: event.target.value })}>
            <option value="">Ninguno</option>
            {materialProcesses.filter((p) => p.id !== editingId).map((p) => <option key={p.id} value={p.id}>{p.description}</option>)}
          </select>
          <span className="mt-1 block text-gray-500">Solo para impresión. No añade costes ni materiales a la factura. Los materiales adicionales se conservan desde la primera impresión.</span>
        </label>
        <Button className="mt-4" type="submit">
          <Save className="mr-2 h-4 w-4" />
          Guardar proceso
        </Button>
      </form>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Buscar proceso" />
        </div>
        <div className="space-y-2">
          {loading ? (
            <p>Cargando procesos...</p>
          ) : processes.length === 0 ? (
            <p className="text-sm text-gray-500">No hay procesos.</p>
          ) : (
            processes.map((process) => (
              <div key={process.id} className="flex flex-col justify-between gap-3 rounded border p-3 md:flex-row md:items-center">
                <div>
                  <div className="font-medium">{process.description}</div>
                  <div className="text-sm text-gray-600">
                    Código {process.legacy_code} · Orden en cuadrante: {process.planning_order ?? "Sin orden"} · {process.is_active ? "Activo" : "Inactivo"}
                    {process.is_overhead ? " · Estructura / gastos generales" : ""}
                    {process.excludes_manufacturing ? " · Ocultar en el seguimiento de fabricación (mantiene el coste)" : ""}
                    {process.albaran_material_process_id ? ` · Materiales en albarán: ${materialProcesses.find((p) => p.id === process.albaran_material_process_id)?.description || "Proceso vinculado"}` : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" title="Editar" onClick={() => edit(process)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => remove(process)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function MaterialSupplierEditor({ material }) {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [suppliers, setSuppliers] = React.useState([]);
  const [editingId, setEditingId] = React.useState(null);
  const [formData, setFormData] = React.useState(initialMaterialSupplier);
  const { toast } = useToast();

  const loadRows = React.useCallback(async () => {
    if (!material?.id) return;
    const [rowResult, supplierResult] = await Promise.all([fetchMaterialSuppliers(material.id), fetchSuppliers()]);
    if (rowResult.error || supplierResult.error) {
      toast({ title: "Error", description: "No se pudieron cargar proveedores del material", variant: "destructive" });
    }
    setRows(rowResult.data || []);
    setSuppliers(supplierResult.data || []);
  }, [material?.id, toast]);

  React.useEffect(() => {
    loadRows();
  }, [loadRows]);

  const reset = () => {
    setEditorOpen(false);
    setEditingId(null);
    setFormData(initialMaterialSupplier);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!formData.supplier_id) {
      toast({ title: "Validación", description: "Selecciona un proveedor", variant: "destructive" });
      return;
    }
    if (Number(formData.purchase_price || 0) < 0) {
      toast({ title: "Validación", description: "El precio no puede ser negativo", variant: "destructive" });
      return;
    }
    const result = await saveMaterialSupplier({ ...formData, material_id: material.id }, editingId);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar proveedor", variant: "destructive" });
      return;
    }
    toast({ title: "Proveedor habitual guardado" });
    reset();
    await loadRows();
  };

  const edit = (row) => {
    setEditorOpen(true);
    setEditingId(row.id);
    setFormData({
      supplier_id: row.supplier_id || "",
      supplier_ref: row.supplier_ref || "",
      supplier_description: row.supplier_description || "",
      purchase_price: row.purchase_price || 0,
      currency: row.currency || "EUR",
      is_primary: Boolean(row.is_primary),
      valid_from: row.valid_from || "",
      valid_until: row.valid_until || "",
    });
  };

  const remove = async (row) => {
    if (!window.confirm("¿Eliminar este proveedor habitual?")) return;
    const result = await deleteMaterialSupplier(row.id);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo eliminar el proveedor", variant: "destructive" });
      return;
    }
    await loadRows();
  };

  if (!material?.id) return null;

  return (
    <div className="mt-4 rounded-lg border bg-gray-50 p-4">
      <h4 className="mb-3 font-semibold">Proveedores habituales de {material.internal_description}</h4>
      <Button type="button" onClick={() => { reset(); setEditorOpen(true); }}><Plus className="mr-2 h-4 w-4" />Añadir proveedor habitual</Button>
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar proveedor habitual" : "Añadir proveedor habitual"}</DialogTitle><DialogDescription>Modifica los datos y pulsa Guardar para conservar los cambios.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="grid gap-3 md:grid-cols-6">
        <select
          className="rounded-md border border-input bg-background px-3 py-2 md:col-span-2"
          value={formData.supplier_id}
          onChange={(event) => setFormData({ ...formData, supplier_id: event.target.value })}
        >
          <option value="">Proveedor</option>
          {suppliers.map((supplier) => (
            <option key={supplier.id} value={supplier.id}>
              {supplier.erp_code} · {supplier.name}
            </option>
          ))}
        </select>
        <Input
          placeholder="Ref."
          value={formData.supplier_ref}
          onChange={(event) => setFormData({ ...formData, supplier_ref: event.target.value })}
        />
        <Input
          placeholder="Descripción proveedor"
          value={formData.supplier_description}
          onChange={(event) => setFormData({ ...formData, supplier_description: event.target.value })}
        />
        <Input
          type="number"
          step="0.0001"
          min="0"
          placeholder="Precio"
          value={formData.purchase_price}
          onChange={(event) => setFormData({ ...formData, purchase_price: event.target.value })}
        />
        <Button type="submit">
          <Plus className="mr-2 h-4 w-4" />
          {editingId ? "Actualizar" : "Añadir"}
        </Button>
        <div className="md:col-span-6 flex flex-wrap gap-4">
          <ToggleField
            label="Proveedor principal"
            checked={formData.is_primary}
            onChange={(value) => setFormData({ ...formData, is_primary: value })}
          />
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Cancelar edición
            </Button>
          )}
        </div>
      </form>
        </DialogContent>
      </Dialog>

      <div className="mt-4 space-y-2">
        {rows.map((row) => (
          <div key={row.id} className="flex flex-col justify-between gap-2 rounded border bg-white p-3 md:flex-row md:items-center">
            <div>
              <div className="font-medium">
                {row.supplier?.erp_code} · {row.supplier?.name}
                {row.is_primary && <span className="ml-2 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700">Principal</span>}
              </div>
              <div className="text-sm text-gray-600">
                {row.supplier_ref || "Sin ref."} · {row.supplier_description || "Sin descripción"} · {formatMoney(row.purchase_price)}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" title="Editar" onClick={() => edit(row)}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="destructive" size="sm" onClick={() => remove(row)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-gray-500">Sin proveedores habituales.</p>}
      </div>
    </div>
  );
}

function MaterialManager() {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [materials, setMaterials] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [editingId, setEditingId] = React.useState(null);
  const [selectedMaterial, setSelectedMaterial] = React.useState(null);
  const [formData, setFormData] = React.useState(initialMaterial);
  const { toast } = useToast();

  const loadMaterials = React.useCallback(async () => {
    const result = await fetchMaterials({ searchTerm });
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar los materiales", variant: "destructive" });
    }
    setMaterials(result.data || []);
    setSelectedMaterial((current) => {
      if (!current) return null;
      return result.data?.find((item) => item.id === current.id) || null;
    });
  }, [searchTerm, toast]);

  React.useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  const reset = () => {
    setEditorOpen(false);
    setEditingId(null);
    setFormData(initialMaterial);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!formData.internal_description.trim()) {
      toast({ title: "Validación", description: "La descripción del material es obligatoria", variant: "destructive" });
      return;
    }
    const result = await saveMaterial(formData, editingId);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar material", variant: "destructive" });
      return;
    }
    toast({ title: editingId ? "Material actualizado" : "Material creado" });
    reset();
    await loadMaterials();
  };

  const edit = (material) => {
    setEditorOpen(true);
    setEditingId(material.id);
    setSelectedMaterial(material);
    setFormData({
      code: material.code || "",
      internal_description: material.internal_description || "",
      season_code: material.season_code || "",
      default_unit: material.default_unit || "ud",
      is_active: material.is_active !== false,
      hide_from_planning: material.hide_from_planning === true,
    });
  };

  const remove = async (material) => {
    if (!window.confirm(`¿Eliminar el material "${material.internal_description}"?`)) return;
    const result = await deleteMaterial(material.id);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo eliminar. Puede estar usado en escandallos.", variant: "destructive" });
      return;
    }
    if (selectedMaterial?.id === material.id) setSelectedMaterial(null);
    await loadMaterials();
  };

  const toggleActive = async (material) => {
    const nextIsActive = material.is_active === false;
    const result = await saveMaterial(
      {
        code: material.code ?? "",
        internal_description: material.internal_description || "",
        season_code: material.season_code || "",
        default_unit: material.default_unit || "ud",
        is_active: nextIsActive,
      },
      material.id
    );
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo actualizar el estado del material", variant: "destructive" });
      return;
    }
    toast({ title: nextIsActive ? "Material activado" : "Material desactivado" });
    await loadMaterials();
  };

  return (
    <div className="space-y-4">
      <Button type="button" onClick={() => { reset(); setEditorOpen(true); }}><Plus className="mr-2 h-4 w-4" />Nuevo material</Button>
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar material" : "Nuevo material"}</DialogTitle><DialogDescription>Modifica los datos y pulsa Guardar para conservar los cambios.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{editingId ? "Editar material" : "Nuevo material"}</h3>
          {editingId && (
            <Button type="button" variant="ghost" onClick={reset}>
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          <Input
            type="number"
            placeholder="Código"
            value={formData.code}
            onChange={(event) => setFormData({ ...formData, code: event.target.value })}
          />
          <Input
            required
            className="md:col-span-2"
            placeholder="Descripción interna"
            value={formData.internal_description}
            onChange={(event) => setFormData({ ...formData, internal_description: event.target.value })}
          />
          <Input
            placeholder="Temporada"
            value={formData.season_code}
            onChange={(event) => setFormData({ ...formData, season_code: event.target.value })}
          />
          <Input
            placeholder="Unidad"
            value={formData.default_unit}
            onChange={(event) => setFormData({ ...formData, default_unit: event.target.value })}
          />
        </div>
        <div className="mt-3">
          <ToggleField label="Activo" checked={formData.is_active} onChange={(value) => setFormData({ ...formData, is_active: value })} />
          <ToggleField label="Ocultar en informe interactivo y cuadrante de O/F" checked={formData.hide_from_planning} onChange={(value) => setFormData({ ...formData, hide_from_planning: value })} />
          <p className="mt-1 text-xs text-gray-500">Se aplica también a órdenes existentes al abrir o actualizar las vistas. Conserva los costes y los pedidos registrados.</p>
        </div>
        <Button className="mt-4" type="submit">
          <Save className="mr-2 h-4 w-4" />
          Guardar material
        </Button>
      </form>
      {selectedMaterial && editingId && <MaterialSupplierEditor key={selectedMaterial.id} material={selectedMaterial} />}
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Buscar material" />
        </div>
        <div className="grid gap-3">
          {materials.map((material) => (
            <div
              key={material.id}
              className={`rounded border p-3 ${material.is_active !== false ? "bg-green-50" : "bg-red-50"} ${selectedMaterial?.id === material.id ? "border-blue-400" : ""}`}
            >
              <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
                <button type="button" className="text-left" onClick={() => edit(material)}>
                  <div className="font-medium">
                    <span className="font-mono text-gray-600">{material.code}</span> · {material.internal_description}
                  </div>
                  <div className="text-sm text-gray-600">
                    {material.default_unit} · {material.season_code || "Sin temporada"} ·{" "}
                    <span className={material.is_active !== false ? "text-green-700" : "text-gray-500"}>
                      {material.is_active !== false ? "Activo" : "Inactivo"}
                      {material.hide_from_planning && " · Oculto en necesidades de O/F"}
                    </span>
                  </div>
                </button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    title={material.is_active !== false ? "Desactivar material" : "Activar material"}
                    onClick={() => toggleActive(material)}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" title="Editar" onClick={() => edit(material)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => remove(material)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

            </div>
          ))}
          {materials.length === 0 && <p className="text-sm text-gray-500">No hay materiales.</p>}
        </div>
      </div>
    </div>
  );
}

export function MaterialsProcesses({ initialTab = "materials", showNavigation = true }) {
  const [activeTab, setActiveTab] = React.useState(initialTab);
  const selectedTab = showNavigation ? activeTab : initialTab;
  const activeClass = (tab) =>
    `rounded-md px-4 py-2 text-sm font-medium ${activeTab === tab ? "bg-primary text-primary-foreground" : "bg-white text-gray-700 border"}`;

  return (
    <div className="space-y-4">
      {showNavigation && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={activeClass("families")} onClick={() => setActiveTab("families")}>
            Familias
          </button>
          <button type="button" className={activeClass("seasons")} onClick={() => setActiveTab("seasons")}>
            Temporadas
          </button>
          <button type="button" className={activeClass("garment-types")} onClick={() => setActiveTab("garment-types")}>Tipos de prenda</button>
          <button type="button" className={activeClass("materials")} onClick={() => setActiveTab("materials")}>
            Materiales
          </button>
          <button type="button" className={activeClass("processes")} onClick={() => setActiveTab("processes")}>
            Procesos
          </button>
        </div>
      )}
      {selectedTab === "garment-types" ? (
        <GarmentTypeManager />
      ) : selectedTab === "families" ? (
        <FamilyManager />
      ) : selectedTab === "seasons" ? (
        <SeasonManager />
      ) : selectedTab === "materials" ? (
        <MaterialManager />
      ) : (
        <ProcessManager />
      )}
    </div>
  );
}
